<template>
    <div>
        <div class="content-header row d-print-none">
            <div class="content-header-left col-md-7 col-12 mb-2">
                <div class="row breadcrumbs-top">
                    <div class="col-12">
                        <h2 class="content-header-title float-left mb-0">Reconciliation Detail</h2>
                        <div class="breadcrumb-wrapper col-12">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item"><router-link to="/home">{{ trans('general.home') }}</router-link></li>
                                <li class="breadcrumb-item"><router-link to="/monnify-reconciliation">Monnify Reconciliation</router-link></li>
                                <li class="breadcrumb-item active">Detail</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
            <div class="content-header-right text-md-right col-md-5 col-12 d-md-block d-none">
                <button class="btn btn-outline-secondary btn-sm mr-1" @click="printPage"><i class="feather icon-printer"></i> Print</button>
                <button class="btn btn-primary btn-sm" @click="$router.push('/monnify-reconciliation')"><i class="feather icon-list"></i> Back</button>
            </div>
        </div>

        <div class="content-body" v-if="row">
            <div class="alert alert-warning d-print-none">This page is read-only and does not correct payment, invoice, or receipt records.</div>

            <div class="row">
                <div class="col-md-4">
                    <div class="card h-100">
                        <div class="card-header"><h4 class="card-title mb-0">Monnify Spreadsheet Data</h4></div>
                        <div class="card-body">
                            <dl class="detail-list">
                                <dt>Transaction Reference</dt><dd>{{ row.transaction_reference || '-' }}</dd>
                                <dt>Session ID</dt><dd>{{ row.session_id || '-' }}</dd>
                                <dt>Account Transaction ID</dt><dd>{{ row.account_transaction_id || '-' }}</dd>
                                <dt>Transaction Record ID</dt><dd>{{ row.transaction_record_id || '-' }}</dd>
                                <dt>Source Account Name</dt><dd>{{ row.source_account_name || '-' }}</dd>
                                <dt>Expected Application Amount</dt><dd>{{ expectedAmount }}</dd>
                                <dt>Amount Paid</dt><dd>{{ row.amount_paid | formatMoney }}</dd>
                                <dt>Difference</dt><dd>{{ differenceAmount }}</dd>
                                <dt>Monnify Created Date</dt><dd>{{ row.external_created_on || '-' }}</dd>
                                <dt>Rejection Reason</dt><dd>{{ row.rejection_reason || '-' }}</dd>
                                <dt>Refund Status</dt><dd><span :class="refundClass(row.refund_status_category)">{{ row.refund_status || 'Unavailable in source file' }}</span></dd>
                            </dl>
                            <h5>Original Row</h5>
                            <pre class="payload-box">{{ rawRow }}</pre>
                        </div>
                    </div>
                </div>

                <div class="col-md-4">
                    <div class="card h-100">
                        <div class="card-header"><h4 class="card-title mb-0">Application Data</h4></div>
                        <div class="card-body">
                            <dl class="detail-list">
                                <dt>Payment ID</dt><dd>{{ paymentValue('id') || '-' }}</dd>
                                <dt>Invoice Number</dt><dd>{{ paymentValue('invoice_no') || '-' }}</dd>
                                <dt>Application Transaction</dt><dd>{{ paymentValue('trx') || '-' }}</dd>
                                <dt>Application Reference</dt><dd>{{ paymentValue('reference') || '-' }}</dd>
                                <dt>RRR</dt><dd>{{ paymentValue('rrr') || '-' }}</dd>
                                <dt>Current Payment Status</dt><dd>{{ row.current_payment_status || '-' }}</dd>
                                <dt>Current Invoice Status</dt><dd>{{ row.current_invoice_status || '-' }}</dd>
                                <dt>Payment Amount</dt><dd>{{ paymentAmount }}</dd>
                                <dt>Paid On</dt><dd>{{ paymentValue('paid_on') || '-' }}</dd>
                                <dt>Payer</dt><dd>{{ paymentValue('payer_name') || row.payer_name || '-' }}</dd>
                                <dt>Institution</dt><dd>{{ institutionName }}</dd>
                                <dt>Receipt</dt><dd>{{ row.receipt_number || 'Receipt relationship not confirmed' }}</dd>
                            </dl>

                            <h5>Payment Items</h5>
                            <table class="table table-sm table-bordered" v-if="paymentItems.length">
                                <thead><tr><th>Item</th><th>Amount</th></tr></thead>
                                <tbody>
                                    <tr v-for="item in paymentItems" :key="item.id">
                                        <td>{{ item.pay_item ? item.pay_item.name : item.description }}</td>
                                        <td>{{ item.amount | formatMoney }}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <p v-else class="text-muted">No payment items found.</p>
                        </div>
                    </div>
                </div>

                <div class="col-md-4">
                    <div class="card h-100">
                        <div class="card-header"><h4 class="card-title mb-0">Comparison</h4></div>
                        <div class="card-body">
                            <dl class="detail-list">
                                <dt>Match Method</dt><dd>{{ row.match_method || '-' }}</dd>
                                <dt>Match Status</dt><dd>{{ row.match_status || '-' }}</dd>
                                <dt>Classification</dt><dd><span :class="classificationClass(displayClassification)">{{ displayClassification }}</span></dd>
                                <dt>Eligibility</dt><dd>{{ row.eligibility_status || '-' }}</dd>
                                <dt>Exception</dt><dd>{{ row.exception_reason || '-' }}</dd>
                                <dt>Recommended Future Action</dt><dd>{{ row.recommended_action || '-' }}</dd>
                                <dt>Calculated Classification</dt><dd>{{ row.calculated_classification || '-' }}</dd>
                                <dt>Classification Agreement</dt><dd>{{ row.classification_agreement_status || '-' }}</dd>
                                <dt>Duplicate Source Reference</dt><dd>{{ row.is_duplicate_source_reference ? row.source_reference_row_count + ' rows' : 'No' }}</dd>
                            </dl>

                            <div class="comparison-box" :class="{ ok: row.match_method === 'payments.trx' }">
                                Reference match: {{ row.match_method === 'payments.trx' ? 'Primary payments.trx match' : 'Not primary payments.trx match' }}
                            </div>
                            <div class="comparison-box" :class="{ warn: row.match_status !== 'unmatched' && row.difference_amount > 0 }">
                                Amount comparison: {{ amountComparisonText }}
                            </div>
                            <div class="comparison-box" :class="{ warn: row.current_payment_status === 'success' || row.current_payment_status === 'PAID' }">
                                Status comparison: {{ row.current_payment_status || 'Unknown' }}
                            </div>
                            <div class="comparison-box" :class="{ warn: row.receipt_number }">
                                Receipt relationship: {{ row.receipt_number || 'Not confirmed' }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card mt-2" v-if="duplicateRows.length">
                <div class="card-header"><h4 class="card-title mb-0">Duplicate Source Rows For This Reference</h4></div>
                <div class="card-body">
                    <div class="alert alert-info">
                        Duplicate source rows are retained separately and are not automatically merged or selected for correction.
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm table-bordered text-nowrap">
                            <thead>
                                <tr>
                                    <th>Source Row</th><th>Session ID</th><th>Created On</th><th>Amount Paid</th><th>Source Name</th><th>Refund</th><th>Match</th><th>Different Amounts</th><th>Different Names</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="duplicate in duplicateRows" :key="duplicate.id">
                                    <td>{{ duplicate.source_row_number }}</td>
                                    <td>{{ duplicate.session_id || '-' }}</td>
                                    <td>{{ duplicate.external_created_on || '-' }}</td>
                                    <td>{{ duplicate.amount_paid | formatMoney }}</td>
                                    <td>{{ duplicate.source_account_name || '-' }}</td>
                                    <td>{{ duplicate.refund_status || 'Unavailable' }}</td>
                                    <td>{{ duplicate.match_method || '-' }} / {{ duplicate.match_status || '-' }}</td>
                                    <td>{{ duplicate.duplicate_group_has_different_amounts ? 'Yes' : 'No' }}</td>
                                    <td>{{ duplicate.duplicate_group_has_different_payer_names ? 'Yes' : 'No' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    const formatNaira = value => {
        if (value === null || value === undefined || value === '') {
            return '₦0.00';
        }

        const amount = Number(value);
        if (!Number.isFinite(amount)) {
            return '₦0.00';
        }

        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    export default {
        data() {
            return { row: null, duplicateRows: [] };
        },
        mounted() {
            if (!helper.hasPermission('view-monnify-reconciliation')) {
                helper.notAccessibleMsg();
                this.$router.push('/home');
            }
            this.getRow();
        },
        computed: {
            rawRow() {
                if (!this.row || !this.row.raw_row_json) return '-';
                try { return JSON.stringify(JSON.parse(this.row.raw_row_json), null, 2); } catch (e) { return this.row.raw_row_json; }
            },
            paymentItems() {
                return this.row && this.row.payment ? (this.row.payment.payment_items || []) : [];
            },
            institutionName() {
                if (!this.row || !this.row.payment) return 'Not available - payment not matched';
                if (this.row.institution) return this.row.institution.name || 'Unknown Institution';
                if (this.row.payment.mda) return this.row.payment.mda.name || 'Unknown Institution';
                const item = (this.row.payment.payment_items || []).find(paymentItem => paymentItem.mda);
                return item && item.mda ? item.mda.name || 'Unknown Institution' : 'Unknown Institution';
            },
            expectedAmount() {
                return this.row && this.row.match_status === 'unmatched' ? 'Not available' : formatNaira(this.row ? this.row.invoice_amount : 0);
            },
            differenceAmount() {
                return this.row && this.row.match_status === 'unmatched' ? 'Not calculated' : formatNaira(this.row ? this.row.difference_amount : 0);
            },
            paymentAmount() {
                return this.row && this.row.payment ? formatNaira(this.row.payment.amount) : '-';
            },
            displayClassification() {
                return this.row && this.row.match_status === 'unmatched' ? 'Cannot classify until payment is matched' : (this.row ? this.row.classification : '-');
            },
            amountComparisonText() {
                if (this.row && this.row.match_status === 'unmatched') {
                    return 'Not calculated until payment is matched';
                }

                return this.row && this.row.difference_amount > 0 ? 'Mismatch detected' : 'No amount difference recorded';
            }
        },
        methods: {
            getRow() {
                axios.get(`/api/monnify-reconciliation/${this.$route.params.id}`)
                    .then(response => response.data)
                    .then(response => {
                        this.row = response.row;
                        this.duplicateRows = response.duplicate_rows || [];
                    })
                    .catch(error => {
                        helper.showDataErrorMsg(error);
                        this.$router.push('/monnify-reconciliation');
                    });
            },
            paymentValue(field) {
                return this.row && this.row.payment ? this.row.payment[field] : null;
            },
            classificationClass(value) {
                if (value === 'underpayment') return 'badge badge-warning';
                if (value === 'overpayment') return 'badge badge-danger';
                if (value === 'exact_amount_rejected' || value === 'exact_amount_rejected_or_review') return 'badge badge-info';
                if (value === 'amount_classification_conflict') return 'badge badge-dark';
                if (value === 'cannot_classify_until_payment_matched' || value === 'Cannot classify until payment is matched') return 'badge badge-light-warning';
                return 'badge badge-secondary';
            },
            refundClass(value) {
                if (value === 'refund_completed') return 'badge badge-success';
                if (value === 'refund_exception') return 'badge badge-danger';
                return 'badge badge-warning';
            },
            printPage() { window.print(); }
        },
        filters: {
            formatMoney(value) { return formatNaira(value); }
        }
    }
</script>

<style scoped>
    .detail-list dt { color: #6b7280; font-size: 11px; text-transform: uppercase; }
    .detail-list dd { margin-bottom: 10px; font-weight: 600; }
    .payload-box { background: #f8fafc; border: 1px solid #d8dee9; padding: 10px; white-space: pre-wrap; }
    .comparison-box { border: 1px solid #d8dee9; padding: 10px; margin-bottom: 8px; border-radius: 6px; }
    .comparison-box.ok { border-color: #28c76f; background: #edfdf5; }
    .comparison-box.warn { border-color: #ff9f43; background: #fff7ed; }
    @media print { .card { box-shadow: none !important; border: 1px solid #d8dee9; } }
</style>
